import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';

const langMap: Record<string, string> = {
  zh: '简体中文',
  en: 'English',
  'zh-Hant': '繁體中文',
};

const langList = [
  { label: '简体中文', value: 'zh' },
  { label: 'English', value: 'en' },
  { label: '繁體中文', value: 'zh-Hant' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common']);
  const { showToast } = useToast();
  const [notify, setNotify] = useState({
    price: true,
    trade: true,
    system: false,
  });
  const [showLogout, setShowLogout] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [showTradePassword, setShowTradePassword] = useState(false);
  const [tradePwdInput, setTradePwdInput] = useState('');
  const [tradePwdConfirm, setTradePwdConfirm] = useState('');
  const [hasTradePwd, setHasTradePwd] = useState(false);
  const pwdRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from('profiles').select('trade_password').eq('id', user.id).maybeSingle();
        setHasTradePwd(!!data?.trade_password);
      } catch {
        // ignore
      }
    })();
  }, []);

  const toggle = (key: keyof typeof notify) => {
    setNotify((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectLang = (value: string) => {
    i18n.changeLanguage(value);
    setShowLangPicker(false);
    showToast(t('common:success'), 'success');
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      <SubHeader title={t('common:settings') || '设置'} />

      {/* Account Security */}
      <div className="px-4 mt-4">
        <h3 className="text-brown-500 text-xs font-medium mb-2 px-1">{t('common:accountSecurity') || '账号与安全'}</h3>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {[
            { icon: 'ri-lock-password-line', label: t('common:changePassword') || '修改登录密码', action: () => showToast(t('common:verifyThenChange') || '请通过验证码验证后修改', 'info') },
            { icon: 'ri-shield-keyhole-line', label: hasTradePwd ? '修改操作密码' : '设置操作密码', action: () => setShowTradePassword(true) },
            { icon: 'ri-smartphone-line', label: t('common:changePhone') || '更换手机号', action: () => showToast(t('common:verifyThenChange') || '请通过验证码验证后更换', 'info') },
            { icon: 'ri-shield-user-line', label: t('common:realNameVerify') || '实名认证', action: () => navigate('/profile/verify'), value: t('common:verified') || '已认证' },
          ].map((item, idx) => (
            <button
              key={item.label}
              onClick={item.action}
              className={`w-full flex items-center justify-between px-4 py-3.5 cursor-pointer text-left ${
                idx !== 2 ? 'border-b border-brown-50' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-gold-600">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                <span className="text-brown-800 text-sm">{item.label}</span>
              </div>
              <div className="flex items-center gap-1">
                {item.value && <span className="text-brown-400 text-xs">{item.value}</span>}
                <span className="w-4 h-4 flex items-center justify-center text-brown-300">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications */}
      <div className="px-4 mt-5">
        <h3 className="text-brown-500 text-xs font-medium mb-2 px-1">{t('common:notifications') || '消息通知'}</h3>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {[
            { key: 'price' as const, label: t('common:priceAlert') || '金价异动提醒', desc: t('common:priceAlertDesc') || '价格大幅波动时通知' },
            { key: 'trade' as const, label: t('common:tradeAlert') || '交易状态提醒', desc: t('common:tradeAlertDesc') || '锁价、支付、交割进度' },
            { key: 'system' as const, label: t('common:systemPush') || '系统消息推送', desc: t('common:systemPushDesc') || '公告及活动信息' },
          ].map((item, idx) => (
            <div
              key={item.key}
              className={`flex items-center justify-between px-4 py-3.5 ${
                idx !== 2 ? 'border-b border-brown-50' : ''
              }`}
            >
              <div>
                <p className="text-brown-800 text-sm">{item.label}</p>
                <p className="text-brown-400 text-xs mt-0.5">{item.desc}</p>
              </div>
              <button
                onClick={() => toggle(item.key)}
                className={`w-11 h-6 rounded-full p-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                  notify[item.key] ? 'bg-gold-500' : 'bg-brown-200'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full bg-white shadow-sm block transition-transform ${
                    notify[item.key] ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* General */}
      <div className="px-4 mt-5">
        <h3 className="text-brown-500 text-xs font-medium mb-2 px-1">{t('common:general') || '通用'}</h3>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {[
            { icon: 'ri-global-line', label: t('common:language') || '语言', value: langMap[i18n.language] || '简体中文', action: () => setShowLangPicker(true) },
            { icon: 'ri-delete-bin-6-line', label: t('common:clearCache') || '清除缓存', value: '12.6 MB', action: () => showToast(t('common:cacheCleared') || '缓存已清除') },
            { icon: 'ri-information-line', label: t('common:about') || '关于CJJY', value: 'v2.6.1', action: () => showToast('CJJY v2.6.1', 'info') },
          ].map((item, idx) => (
            <button
              key={item.label}
              onClick={item.action}
              className={`w-full flex items-center justify-between px-4 py-3.5 cursor-pointer text-left ${
                idx !== 2 ? 'border-b border-brown-50' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-gold-600">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                <span className="text-brown-800 text-sm">{item.label}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-brown-400 text-xs">{item.value}</span>
                <span className="w-4 h-4 flex items-center justify-center text-brown-300">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Logout */}
      <div className="px-4 mt-6">
        <button
          onClick={() => setShowLogout(true)}
          className="w-full py-3 rounded-xl bg-white text-red-500 text-sm font-medium shadow-sm cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
        >
          {t('common:logout') || '退出登录'}
        </button>
      </div>

      {/* Language Picker */}
      <BottomSheet open={showLangPicker} onClose={() => setShowLangPicker(false)} title={t('common:language') || '语言'}>
        <div className="px-4 pb-6">
          {langList.map((lang) => (
            <button
              key={lang.value}
              onClick={() => selectLang(lang.value)}
              className={`w-full text-left px-4 py-3.5 rounded-xl text-sm transition-colors mb-1 cursor-pointer whitespace-nowrap ${
                i18n.language === lang.value
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-brown-700 hover:bg-brown-50'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Trade Password Sheet */}
      <BottomSheet open={showTradePassword} onClose={() => { setShowTradePassword(false); setTradePwdInput(''); setTradePwdConfirm(''); }} title={hasTradePwd ? '修改操作密码' : '设置操作密码'}>
        <div className="px-4 pb-8">
          <div className="mb-5">
            <p className="text-brown-500 text-xs text-center mb-3">设置6位数字操作密码，用于敏感操作验证</p>
            <div
              className="flex justify-center gap-2 relative cursor-text"
              onClick={() => pwdRef.current?.focus()}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-colors pointer-events-none ${
                    i < tradePwdInput.length
                      ? 'border-gold-400 bg-gold-50 text-brown-900'
                      : 'border-gray-200 text-transparent'
                  }`}
                >
                  {i < tradePwdInput.length ? '●' : ''}
                </div>
              ))}
              <input
                ref={pwdRef}
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={tradePwdInput}
                onChange={(e) => setTradePwdInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-text"
                autoFocus
              />
            </div>
          </div>

          <div className="mb-6">
            <p className="text-brown-500 text-xs text-center mb-3">确认操作密码</p>
            <div
              className="flex justify-center gap-2 relative cursor-text"
              onClick={() => confirmRef.current?.focus()}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-colors pointer-events-none ${
                    i < tradePwdConfirm.length
                      ? 'border-gold-400 bg-gold-50 text-brown-900'
                      : 'border-gray-200 text-transparent'
                  }`}
                >
                  {i < tradePwdConfirm.length ? '●' : ''}
                </div>
              ))}
              <input
                ref={confirmRef}
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={tradePwdConfirm}
                onChange={(e) => setTradePwdConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-text"
              />
            </div>
          </div>

          <button
            onClick={async () => {
              if (tradePwdInput.length !== 6) {
                showToast('请输入6位数字密码', 'error');
                return;
              }
              if (tradePwdConfirm.length !== 6) {
                showToast('请输入确认密码', 'error');
                return;
              }
              if (tradePwdInput !== tradePwdConfirm) {
                showToast('两次输入不一致，请重新输入', 'error');
                setTradePwdInput('');
                setTradePwdConfirm('');
                return;
              }
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) throw new Error('登录已失效');
                const { error } = await supabase
                  .from('profiles')
                  .update({ trade_password: tradePwdConfirm })
                  .eq('id', user.id);
                if (error) throw error;
                setHasTradePwd(true);
                setShowTradePassword(false);
                setTradePwdInput('');
                setTradePwdConfirm('');
                showToast('操作密码设置成功', 'success');
              } catch (e) {
                showToast(e instanceof Error ? e.message : '设置失败', 'error');
              }
            }}
            disabled={tradePwdInput.length !== 6 || tradePwdConfirm.length !== 6}
            className={`w-full py-3 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap ${
              tradePwdInput.length !== 6 || tradePwdConfirm.length !== 6 ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
            }`}
          >
            确认设置
          </button>
        </div>
      </BottomSheet>

      {/* Logout Confirm */}
      <BottomSheet open={showLogout} onClose={() => setShowLogout(false)} title={t('common:logout') || '退出登录'}>
        <div className="px-4 pb-8">
          <p className="text-brown-600 text-sm text-center py-4">{t('common:confirmLogout')}</p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLogout(false)}
              className="flex-1 py-3 rounded-full border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                try { localStorage.removeItem('jinan_user_login_at'); } catch { /* ignore */ }
                navigate('/login', { replace: true });
              }}
              className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              {t('common:confirm')}
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}